/**
 * =================================================================
 * LE REFUS D'UN COMPTE TECHNICIEN — ce que le serveur dit, lu tel quel
 *
 * Depuis le 30/10/2026, un compte technicien n'a pas accès au site d'administration : le
 * serveur refuse ses appels (403, code `ESPACE_RESERVE_ADMINISTRATEURS`) et joint le
 * message à montrer et le lien de l'application mobile. Le site n'a RIEN à décider — la
 * garde est côté serveur — il lit ce refus et l'explique, puis ferme la session.
 *
 * Ce module ne fait que cette lecture, sans DOM ni React : ce qu'on accepte (quel statut,
 * quel corps, quel lien) s'essaie ainsi au caractère près.
 * =================================================================
 */

/** Le code du refus d'un compte technicien (contrat du 30/10/2026, `backend/utils/accesTechnicien.js`). */
export const CODE_REFUS_TECHNICIEN = 'ESPACE_RESERVE_ADMINISTRATEURS';

/**
 * Le message du contrat, mot pour mot — le même que `backend/utils/accesTechnicien.js`.
 * Il sert quand le refus arrive sans message (serveur plus ancien, réponse abîmée par un
 * intermédiaire) : mieux vaut le texte du contrat qu'un écran muet.
 */
export const MESSAGE_PAR_DEFAUT =
  "Votre compte est un compte technicien : il n'a pas accès à l'administration de ServUp 4i. Téléchargez l'application ServUp 4i sur votre téléphone Android, puis connectez-vous avec la même adresse e-mail et le même mot de passe.";

/** Ce que l'écran affiche : le message du serveur et, s'il y en a un, le lien de l'APK. */
export interface RefusTechnicien {
  message: string;
  apkUrl: string | null;
}

/** Un objet JSON, ou rien : `null`, une chaîne, un nombre ou un tableau n'en portent pas. */
function objet(valeur: unknown): Record<string, unknown> | null {
  return typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur)
    ? (valeur as Record<string, unknown>)
    : null;
}

/**
 * Le lien de l'application, s'il est utilisable. Seuls `http://` et `https://` passent :
 * cette valeur va telle quelle dans un `href`, et un `javascript:alert(1)` reçu dans une
 * réponse ne doit pas devenir un bouton. Les espaces sont retirés avant le test pour qu'une
 * valeur bricolée ne se glisse pas entre les mailles.
 */
function lienApplication(valeur: unknown): string | null {
  if (typeof valeur !== 'string') return null;
  const lien = valeur.trim();
  return /^https?:\/\//i.test(lien) ? lien : null;
}

/**
 * Le refus d'un compte technicien, ou `null` pour toute autre réponse.
 *
 * Le refus n'est reconnu que sur un **403** ET sur le code du contrat : un 403 `MFA_REQUIRED`
 * ou `MFA_ENROLLMENT_REQUIRED` est un autre sujet (le second facteur, traité plus loin dans
 * la session), et un 401, un 200 ou un 500 ne sont pas des refus d'accès.
 *
 * @param status - le statut HTTP de la réponse
 * @param corps - le corps déjà lu (JSON), de forme inconnue par nature
 */
export function lireRefusTechnicien(status: number, corps: unknown): RefusTechnicien | null {
  if (status !== 403) return null;
  const enveloppe = objet(corps);
  const erreur = enveloppe ? objet(enveloppe.error) : null;
  if (!erreur || erreur.code !== CODE_REFUS_TECHNICIEN) return null;

  return {
    message:
      typeof erreur.message === 'string' && erreur.message.trim() !== ''
        ? erreur.message
        : MESSAGE_PAR_DEFAUT,
    apkUrl: lienApplication(erreur.apk_url)
  };
}
